// Dashboard (the welcome strip, tiles, today's queue, funnel and sources) and CMS Home
// (the tiles and the Follow-Ups Call Queue). Both read the same scoped student list.
import { useState } from 'react';

import { CallQueueScreen } from 'src/front-components/lead-desk/screens-calls';
import { WorkInbox } from 'src/front-components/lead-desk/screens-inbox';
import { DetailsDrawer, EditDrawer } from 'src/front-components/lead-desk/student-drawers';
import { Avatar, DataTable, FilterBar, MeterRow, MultiSelect, Pager, StatusChip, type Column } from 'src/front-components/lead-desk/kit';
import type { DeskContext } from 'src/front-components/lead-desk/context';
import { PageMasthead, ShellCard, ShellEmptyState, ShellGrid, ShellSectionLabel, ShellStat, TabStrip } from 'src/front-components/theme/page-chrome';
import { DeskButton, DeskInput } from 'src/front-components/theme/desk-controls';
import { BRAND, getPalette } from 'src/front-components/theme/ewings-theme';
import { getShellColors, SHELL_RADIUS, SHELL_SPACE, SHELL_TYPE } from 'src/front-components/theme/shell-tokens';
import {
  DESK_STATUS_COLORS,
  DESK_STATUS_LABELS,
  filterStudents,
  FOLLOW_UP_LABELS,
  formatCallDate,
  homeStats,
  inQueue,
  leadFunnel,
  NO_FILTERS,
  paginate,
  queueCounts,
  sortStudents,
  sourceBreakdown,
  SOURCE_LABELS,
  studentPhone,
  UNASSIGNED,
  type QueueTab,
  type StudentFilters,
  type StudentRow,
} from 'src/utils/lead-desk';

const PAGE_SIZE = 10;

const statusOptions = Object.entries(DESK_STATUS_LABELS).map(([value, label]) => ({ value, label }));

/** Name, parent and source, stacked the way the desk shows a student. */
export const StudentCell = ({ scheme, student }: { scheme: string; student: StudentRow }) => {
  const shell = getShellColors(scheme);

  return (
    <div style={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text, display: 'flex', alignItems: 'center', gap: SHELL_SPACE[1], overflow: 'hidden' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{student.name ?? '—'}</span>
        {student.isPotential === true && <StatusChip label="Hot" color="red" />}
      </div>
      <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Parent: {student.parentName ?? 'N/A'}</div>
    </div>
  );
};

export const StatusCell = ({ student }: { student: StudentRow }) => {
  const followUp = student.followUpStatus ? FOLLOW_UP_LABELS[student.followUpStatus] : null;
  const updateDate = student.statusUpdatedAt ? formatCallDate(student.statusUpdatedAt) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, maxWidth: '100%', width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap', minWidth: 0, maxWidth: '100%' }}>
        <StatusChip label={DESK_STATUS_LABELS[student.deskStatus ?? ''] ?? '—'} color={DESK_STATUS_COLORS[student.deskStatus ?? ''] ?? 'gray'} />
        {followUp && (
          <span style={{ fontSize: '11px', fontWeight: 600, color: '#1d4ed8', background: 'rgba(59, 130, 246, 0.12)', padding: '1px 5px', borderRadius: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {followUp}
          </span>
        )}
      </div>
      {(student.latestRemark || updateDate || student.statusUpdatedBy) && (
        <div style={{ fontSize: '11px', color: 'var(--t-font-color-tertiary, #6b7280)', lineHeight: 1.3, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
          {student.latestRemark && (
            <div style={{ fontWeight: 500, color: 'var(--t-font-color-secondary, #374151)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }} title={student.latestRemark}>
              "{student.latestRemark}"
            </div>
          )}
          <div style={{ fontSize: '10px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
            {updateDate && <span>Updated {updateDate}</span>}
            {student.statusUpdatedBy && <span> by {student.statusUpdatedBy}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------- dashboard

export const OverviewScreen = ({ ctx, name }: { ctx: DeskContext; name: string }) => {

  const shell = getShellColors(ctx.scheme);
  const stats = homeStats(ctx.students);
  const today = inQueue(ctx.students, 'TODAY').slice(0, 8);
  const funnel = leadFunnel(ctx.students);
  const sources = sourceBreakdown(ctx.students);
  return (
    <>
      <div
        style={{
          background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.navyLight})`,
          borderLeft: `4px solid ${BRAND.navyLight}`,
          color: '#fff',
          borderRadius: SHELL_RADIUS.md,
          padding: `${SHELL_SPACE[5]} ${SHELL_SPACE[6]}`,
          marginBottom: SHELL_SPACE[4],
        }}
      >
        <div style={{ ...SHELL_TYPE.pageTitle }}>Welcome, {name === '' ? ctx.profile.label : name} 👋</div>
        <div style={{ ...SHELL_TYPE.body, opacity: 0.85, marginTop: SHELL_SPACE[1] }}>You have {stats.scheduledToday} calls pending today.</div>
      </div>

      <WorkInbox ctx={ctx} />
      <div style={{ height: SHELL_SPACE[3] }} />
      <ShellGrid min={150}>
        <ShellStat scheme={ctx.scheme} label="Total students" value={stats.total} />
        <ShellStat scheme={ctx.scheme} label="Today's calls" value={stats.scheduledToday} />
        <ShellStat scheme={ctx.scheme} label="Pending" value={stats.pending} />
        <ShellStat scheme={ctx.scheme} label="Admissions" value={stats.enrolled} />
        <ShellStat scheme={ctx.scheme} label="Interested" value={stats.interested} />
        <ShellStat scheme={ctx.scheme} label="Blocked" value={stats.blocked} />
      </ShellGrid>

      <div style={{ height: SHELL_SPACE[3]  }} />
      <ShellCard scheme={ctx.scheme}>
        <ShellSectionLabel scheme={ctx.scheme}>Today's Follow-Up Queue</ShellSectionLabel>
        {today.length === 0 ? (
          <ShellEmptyState scheme={ctx.scheme} message="No follow-up calls pending today. Outstanding!" />
        ) : (
          today.map((student) => (
            <div
              key={student.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: SHELL_SPACE[2],
                padding: `${SHELL_SPACE[2.5]} 0`,
                borderBottom: `1px solid ${shell.borderLight}`,
                minWidth: 0,
                maxWidth: '100%',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: SHELL_SPACE[2], flex: '1 1 180px', minWidth: 0 }}>
                <Avatar text={(student.name ?? '?').slice(0, 1).toUpperCase()} size={32} />
                <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                  <div style={{ ...SHELL_TYPE.bodyMedium, color: shell.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{student.name}</div>
                  <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary, whiteSpace: 'nowrap' }}>{studentPhone(student)}</div>
                </div>
              </div>
              <div style={{ flex: '1 1 200px', minWidth: 0, maxWidth: '100%' }}>
                <StatusCell student={student} />
              </div>
              <span style={{ ...SHELL_TYPE.caption, color: shell.textSecondary, whiteSpace: 'nowrap', marginLeft: 'auto', background: shell.backgroundSecondary, padding: '2px 8px', borderRadius: 4 }}>
                {formatCallDate(student.nextCallAt)}
              </span>
            </div>
          ))
        )}
      </ShellCard>

      <div style={{ height: SHELL_SPACE[3] }} />
      <ShellGrid min={320}>
        <ShellCard scheme={ctx.scheme}>
          <ShellSectionLabel scheme={ctx.scheme}>My Lead Funnel</ShellSectionLabel>
          {funnel.map((step) => (
            <MeterRow key={step.label} scheme={ctx.scheme} label={step.label} count={step.count} share={step.share} />
          ))}
        </ShellCard>
        <ShellCard scheme={ctx.scheme}>
          <ShellSectionLabel scheme={ctx.scheme}>My Lead Sources</ShellSectionLabel>
          {sources.length === 0 && <ShellEmptyState scheme={ctx.scheme} message="No students yet." />}
          {sources.slice(0, 12).map((source) => (
            <MeterRow key={source.key} scheme={ctx.scheme} label={source.label} count={source.count} share={source.share} />
          ))}
        </ShellCard>
      </ShellGrid>
    </>
  );
};

// ---------------------------------------------------------------------- home

export const HomeScreen = ({ ctx }: { ctx: DeskContext }) => {

  return <ManagerHome ctx={ctx} />;
};

const ManagerHome = ({ ctx }: { ctx: DeskContext }) => {
  const shell = getShellColors(ctx.scheme);
  const stats = homeStats(ctx.students);
  const counts = queueCounts(ctx.students);
  const [tab, setTab] = useState<QueueTab>('TODAY');
  const [filters, setFilters] = useState<StudentFilters>(NO_FILTERS);
  const [name, setName] = useState('');
  const [mobile, setMobile] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<StudentRow | null>(null);

  const rows = filterStudents(inQueue(ctx.students, tab), filters).filter(
    (student) =>
      (name.trim() === '' || (student.name ?? '').toLowerCase().includes(name.trim().toLowerCase())) &&
      (mobile.trim() === '' || studentPhone(student).includes(mobile.trim())),
  );
  const paged = paginate(rows, page, PAGE_SIZE);
  const counsellorLocked = ctx.profile.scope === 'OWN_COUNSELLOR';
  const telecallerOptions = [{ value: UNASSIGNED, label: UNASSIGNED }, ...ctx.telecallers.map((t) => ({ value: t, label: t }))];

  const columns: Column<StudentRow>[] = [
    { key: 'student', header: 'Student Name', width: '200px', render: (s) => <StudentCell scheme={ctx.scheme} student={s} /> },
    {
      key: 'contact',
      header: 'Contact',
      width: '1fr',
      render: (s) => (
        <div>
          <div style={{ color: shell.text }}>{studentPhone(s)}</div>
          <div style={{ ...SHELL_TYPE.caption, color: shell.textTertiary }}>Source: {SOURCE_LABELS[s.leadSource ?? ''] ?? '—'}</div>
        </div>
      ),
    },
    { key: 'status', header: 'Status / Priority', width: '230px', render: (s) => <StatusCell student={s} /> },
    { key: 'counsellor', header: 'Counselor', width: '140px', render: (s) => <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{s.counsellorName ?? UNASSIGNED}</span> },
    { key: 'telecaller', header: 'Telecaller', width: '140px', render: (s) => <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{s.telecallerName ?? UNASSIGNED}</span> },
    { key: 'due', header: 'Scheduled Followup', width: '150px', render: (s) => <span style={{ whiteSpace: 'nowrap' }}>{s.nextCallAt ? formatCallDate(s.nextCallAt) : '—'}</span> },
    { key: 'follow', header: 'Follow-up Status', width: '140px', render: (s) => <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{FOLLOW_UP_LABELS[s.followUpStatus ?? ''] ?? '—'}</span> },
    {
      key: 'actions',
      header: 'Actions',
      width: '90px',
      align: 'right',
      render: (s) => (
        <DeskButton scheme={ctx.scheme} onClick={() => setEditing(s)} title="Edit this student">
          Edit
        </DeskButton>
      ),
    },
  ];

  return (
    <>
      <PageMasthead
        palette={getPalette(ctx.scheme)}
        scheme={ctx.scheme}
        title="Educational CMS Dashboard"
        subtitle="Business intelligence for managing student leads, counselor performance and call activity."
      />
      <ShellGrid min={170}>
        <ShellStat scheme={ctx.scheme} label="Total active leads" value={stats.total} hint="Cumulative lead funnel" />
        <ShellStat scheme={ctx.scheme} label="Calls scheduled today" value={stats.scheduledToday} hint="Pending queue for today" />
        <ShellStat scheme={ctx.scheme} label={ctx.role === 'ADMIN' ? 'Pending follow-ups' : 'My scheduled today'} value={ctx.role === 'ADMIN' ? stats.pending : stats.scheduledToday} hint={ctx.role === 'ADMIN' ? 'Overdue calls' : 'Assigned to you'} />
        <ShellStat scheme={ctx.scheme} label="Potential leads" value={stats.potential} hint="Interested / Warm leads" />
        <ShellStat scheme={ctx.scheme} label="Enrolled" value={stats.enrolled} hint="Enrolled students" />
      </ShellGrid>

      <div style={{ height: SHELL_SPACE[4] }} />
      <WorkInbox ctx={ctx} />
      <div style={{ height: SHELL_SPACE[4] }} />
      <ShellCard scheme={ctx.scheme}>
        <ShellSectionLabel
          scheme={ctx.scheme}
          action={
            <DeskButton scheme={ctx.scheme} primary onClick={() => ctx.go('ADD')}>
              + Add Student
            </DeskButton>
          }
        >
          Follow-Ups Call Queue
        </ShellSectionLabel>

        <TabStrip
          palette={getPalette(ctx.scheme)}
          scheme={ctx.scheme}
          active={tab}
          onSelect={(id) => {
            setTab(id as QueueTab);
            setPage(1);
          }}
          tabs={[
            { id: 'TODAY', label: 'Today', count: counts.TODAY },
            { id: 'UPCOMING', label: 'Upcoming', count: counts.UPCOMING },
            { id: 'PENDING', label: 'Pending', count: counts.PENDING },
            { id: 'CLOSED', label: 'Closed', count: counts.CLOSED },
          ]}
        />

        <FilterBar scheme={ctx.scheme}>
          <DeskInput scheme={ctx.scheme} value={name} onChange={(v2) => { setName(v2); setPage(1); }} placeholder="Search by name..." width="170px" />
          <DeskInput scheme={ctx.scheme} value={mobile} onChange={(v2) => { setMobile(v2); setPage(1); }} placeholder="Search by mobile..." width="170px" />
          <MultiSelect scheme={ctx.scheme} label="Filter by Status" options={statusOptions} selected={filters.statuses} onChange={(statuses) => { setFilters({ ...filters, statuses }); setPage(1); }} />
          <MultiSelect
            scheme={ctx.scheme}
            label="Filter by Counselor"
            options={[{ value: UNASSIGNED, label: UNASSIGNED }, ...ctx.counsellors.map((c) => ({ value: c, label: c }))]}
            selected={filters.counsellors}
            onChange={(counsellors) => { setFilters({ ...filters, counsellors }); setPage(1); }}
            locked={counsellorLocked ? `${ctx.person} (Me)` : undefined}
          />
          <MultiSelect scheme={ctx.scheme} label="Filter by Telecaller" options={telecallerOptions} selected={filters.telecallers} onChange={(telecallers) => { setFilters({ ...filters, telecallers }); setPage(1); }} />
        </FilterBar>

        <div style={{ ...SHELL_TYPE.body, color: shell.textSecondary, marginBottom: SHELL_SPACE[2] }}>
          Showing {rows.length === 0 ? 0 : (paged.page - 1) * PAGE_SIZE + 1} to {Math.min(paged.page * PAGE_SIZE, rows.length)} of {rows.length} entries
        </div>
        <DataTable scheme={ctx.scheme} columns={columns} rows={paged.items} rowKey={(s) => s.id} empty="No scheduled follow-up calls found matching the filters." />
        <Pager scheme={ctx.scheme} page={paged.page} pages={paged.pages} onPage={setPage} summary={`Showing ${paged.items.length} of ${rows.length} entries`} />
      </ShellCard>

      {editing !== null && <EditDrawer ctx={ctx} student={editing} onClose={() => setEditing(null)} />}
    </>
  );
};

export { DetailsDrawer, sortStudents };
